<template>
  <v-card data-testid="eudr-validation">
    <v-card-title class="primary white--text">
      <span class="headline text-truncate">{{ title }}</span>
      <v-spacer/>
      <Icon
        dataTestId="eudr-validation-close"
        icon="mdi-close"
        iconColor="white"
        :tooltipText="$t('close')"
        :small="false"
        :large="true"
        @icon-clicked="$emit('close')"
      />
    </v-card-title>

    <v-card-text class="pa-0">
      <v-alert v-if="isFinalized" type="warning" dense text class="ma-3 mb-0">
        {{ $t('eudrFinalizedEditWarning') }}
      </v-alert>

      <div v-if="loading" class="d-flex align-center justify-center" style="height: 70vh;">
        <v-progress-circular indeterminate color="primary"/>
      </div>

      <div v-else-if="loadError" class="d-flex align-center justify-center grey--text text--darken-1" style="height: 70vh;">
        {{ $t('eudrValidationLoadFailed') }}
      </div>

      <v-row v-else no-gutters>
        <v-col cols="12" md="4">
          <div class="px-4 py-2 d-flex align-center">
            <span class="body-2">{{ $t('eudrIncludedSummary', { included: includedCount, total: rows.length }) }}</span>
            <v-spacer/>
            <v-chip v-if="contestedParcelCount" small color="warning" outlined>
              <v-icon left small>mdi-alert</v-icon>
              {{ contestedParcelCount }}
            </v-chip>
          </div>
          <div class="px-4 pb-1 caption grey--text">{{ $t('eudrReviewKeyHints') }}</div>
          <v-divider/>
          <div class="eudr-validation-panel">
            <template v-for="section in sections">
              <div v-if="section.groups.length" :key="section.key">
                <div
                  class="px-4 pt-3 pb-1 caption font-weight-medium grey--text text--darken-2 d-flex align-center eudr-section-header"
                  @click="toggleSection(section.key)"
                >
                  <v-icon small class="mr-1">{{ collapsedSections[section.key] ? 'mdi-chevron-right' : 'mdi-chevron-down' }}</v-icon>
                  <span>{{ section.title }} ({{ section.count }})</span>
                  <v-spacer/>
                  <v-btn
                    v-if="section.dupeCount"
                    x-small
                    text
                    color="primary"
                    :id="`eudr-exclude-dupes-${section.key}`"
                    @click.stop="excludeDuplicatePoints(section.key)"
                  >{{ $t('eudrExcludeDupePoints', { count: section.dupeCount }) }}</v-btn>
                  <v-btn
                    v-if="section.key === 'covered' && coveredIncludedCount"
                    x-small
                    text
                    color="primary"
                    id="eudr-exclude-covered"
                    @click.stop="excludeAllCovered"
                  >{{ $t('eudrExcludeAllCovered') }}</v-btn>
                </div>
                <!-- Bulk cleanup: exclude points within N meters of a declared shape, then draw the rest. -->
                <div v-if="section.key === 'unmatched' && !collapsedSections.unmatched" class="px-4 py-1 d-flex align-center">
                  <!-- Deliberately NOT v-model: every keystroke would re-render the thousand-row list. -->
                  <v-text-field
                    :value="nearPointThreshold"
                    type="number"
                    dense
                    hide-details
                    class="eudr-distance-threshold"
                    :label="$t('eudrDistanceThresholdLabel')"
                    @input="onThresholdInput"
                    @change="onThresholdInput($event, { immediate: true })"
                  />
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    class="ml-2"
                    id="eudr-exclude-near-points"
                    :disabled="!nearPointExcludeEnabled"
                    @click="excludeNearPoints"
                  >{{ $t('eudrExcludeNearPoints') }}</v-btn>
                  <v-btn
                    x-small
                    outlined
                    color="primary"
                    class="ml-2"
                    id="eudr-draw-remaining-areas"
                    :disabled="!remainingUnmatchedCount"
                    @click="drawAreasForRemaining"
                  >{{ $t('eudrDrawAreas') }}</v-btn>
                  <Icon
                    icon="mdi-information-outline"
                    iconColor="info"
                    margin="ml-1"
                    tooltipClass="eudr-reduce-info-tooltip"
                    :tooltipText="$t('eudrExcludeNearPointsInfo')"
                  />
                </div>
                <template v-if="!collapsedSections[section.key]">
                <div v-for="group in section.groups" :key="`${section.key}-${group.parcelId}`" class="eudr-parcel-group">
                  <div v-if="group.label" class="px-4 py-1 d-flex align-center">
                    <span class="caption font-weight-medium">{{ group.label }}</span>
                    <v-icon v-if="group.conflict" small color="warning" class="ml-1">mdi-alert</v-icon>
                  </div>
                  <div
                    v-for="feature in group.features"
                    :key="feature.featureId"
                    :ref="'row-' + feature.featureId"
                    class="eudr-feature-row d-flex align-center px-4 py-1"
                    :class="{ 'eudr-feature-excluded': isExcluded(feature.featureId), 'eudr-feature-focused': feature.featureId === focusedFeatureId }"
                    @click="zoomTo(feature)"
                  >
                    <v-simple-checkbox
                      :value="!isExcluded(feature.featureId)"
                      color="primary"
                      :ripple="false"
                      @click.stop.prevent="toggle(feature.featureId)"
                    />
                    <div class="flex-grow-1">
                      <div class="body-2">{{ feature.displayName }}</div>
                      <div class="caption grey--text text--darken-1">{{ featureCaption(feature) }}</div>
                    </div>
                    <Icon
                      v-if="warningsFor(feature.featureId).length"
                      icon="mdi-alert-octagon-outline"
                      :iconColor="hasBlockingWarning(feature.featureId) ? 'error' : 'warning'"
                      :tooltipText="warningTooltip(feature.featureId)"
                    />
                    <Icon
                      v-if="feature.isPoint && !feature.covered"
                      :icon="fetchingPointId === feature.featureId ? 'mdi-timer-sand' : 'mdi-map-search'"
                      iconColor="primary"
                      :tooltipText="$t('eudrFetchParcel')"
                      @icon-clicked="fetchParcelForPoint(feature.featureId)"
                    />
                    <Icon
                      v-if="feature.isPoint && !feature.covered"
                      icon="mdi-vector-circle"
                      iconColor="primary"
                      :tooltipText="$t('eudrDrawArea')"
                      @icon-clicked="drawAreaForPoint(feature.featureId)"
                    />
                    <Icon
                      v-if="feature.canRevert"
                      icon="mdi-undo-variant"
                      iconColor="primary"
                      :tooltipText="$t('eudrRevertToPoint')"
                      @icon-clicked="revertToPoint(feature.featureId)"
                    />
                    <Icon
                      icon="mdi-vector-polyline-edit"
                      iconColor="primary"
                      :tooltipText="$t('eudrEditShape')"
                      @icon-clicked="openShapeEditorFor(feature.featureId)"
                    />
                    <v-icon small :color="feature.origin === 'internal' ? 'primary' : undefined">
                      {{ feature.origin === 'internal' ? 'mdi-home' : 'mdi-domain' }}
                    </v-icon>
                  </div>
                </div>
                </template>
              </div>
            </template>
          </div>
        </v-col>

        <v-col cols="12" md="8">
          <EudrMap
            v-if="hasGeometry"
            ref="map"
            :key="mapKey"
            :featureCollection="workingCollection"
            :focusFeatureId="focusedFeatureId"
            interactive
            @feature-click="toggle"
            @feature-edit="openShapeEditorFor"
            @point-moved="onPointMoved"
          />
          <div v-else class="d-flex align-center justify-center grey--text text--darken-1" style="height: 70vh;">
            {{ $t('eudrNoGeometry') }}
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions>
      <v-btn
        text
        color="error"
        id="eudr-validation-reset"
        :disabled="!canReset || resetting || saving"
        :loading="resetting"
        @click="promptReset"
      >{{ $t('eudrResetValidation') }}</v-btn>
      <v-btn
        text
        id="eudr-validation-edit-shapes"
        :disabled="loading || loadError || saving || resetting || finalizing"
        @click="openShapeEditor"
      >
        <v-icon left>mdi-vector-polygon</v-icon>
        {{ $t('eudrEditShapes') }}
      </v-btn>
      <v-spacer/>
      <v-btn text @click="$emit('close')">{{ $t('cancel') }}</v-btn>
      <v-btn
        color="primary"
        id="eudr-validation-save"
        :loading="saving"
        :disabled="loading || loadError || saving || resetting || finalizing"
        @click="save"
      >{{ $t('eudrSaveValidation') }}</v-btn>
      <v-btn
        color="success"
        id="eudr-validation-finalize"
        :loading="finalizing"
        :disabled="loading || loadError || saving || resetting || finalizing"
        @click="openFinalize"
      >
        <v-icon left>mdi-lock-check</v-icon>
        {{ $t('eudrFinalize') }}
      </v-btn>
    </v-card-actions>

    <!-- Finalize: the compliance report for the derived EU submission file -->
    <Dialog :stateId="finalizeDialogId" @close="cancelFinalize" @click:outside="cancelFinalize" @dialog-closing="cancelFinalize">
      <v-card v-if="promptingFinalize && complianceReport">
        <v-card-title class="primary white--text">{{ $t('eudrFinalizeTitle') }}</v-card-title>
        <v-card-text class="pt-4">
          <v-alert :type="complianceReport.blocking ? 'error' : 'success'" dense text>
            {{ complianceReport.blocking ? $t('eudrComplianceBlocked') : $t('eudrComplianceReady') }}
          </v-alert>
          <v-alert v-if="contestedParcelCount && !complianceReport.blocking" type="warning" dense text>
            {{ $t('eudrFinalizeWithConflicts') }}
          </v-alert>
          <div class="body-2">
            <p class="mb-1">{{ $t('eudrComplianceIncluded', { count: complianceReport.includedFeatures, excluded: complianceReport.excludedFeatures }) }}</p>
            <p class="mb-1">{{ $t('eudrComplianceVertices', { before: complianceReport.verticesBefore, after: complianceReport.verticesAfter, max: complianceReport.maxFeatureVertices }) }}</p>
            <p class="mb-1">{{ $t('eudrComplianceSize', { size: complianceSizeLabel }) }}</p>
            <p v-if="complianceReport.holesSplit" class="mb-1">{{ $t('eudrComplianceHolesSplit', { count: complianceReport.holesSplit }) }}</p>
            <p v-if="complianceReport.holesStripped" class="mb-1 warning--text">{{ $t('eudrComplianceHoles', { count: complianceReport.holesStripped }) }}</p>
            <p v-if="complianceReport.ringsClosed" class="mb-1">{{ $t('eudrComplianceRingsClosed', { count: complianceReport.ringsClosed }) }}</p>
            <p v-if="complianceReport.duplicatePointsRemoved" class="mb-1">{{ $t('eudrComplianceDuplicates', { count: complianceReport.duplicatePointsRemoved }) }}</p>
            <p v-if="complianceReport.simplifiedFeatures" class="mb-1">{{ $t('eudrComplianceSimplified', { count: complianceReport.simplifiedFeatures }) }}</p>
            <p v-if="complianceReport.undersizedPartsRemoved" class="mb-1">{{ $t('eudrComplianceUndersizedParts', { count: complianceReport.undersizedPartsRemoved }) }}</p>
            <p v-if="complianceReport.undersizedFeatures.length" class="mb-1 warning--text">{{ $t('eudrComplianceUndersizedFeatures', { names: complianceReport.undersizedFeatures.join(', ') }) }}</p>
            <p v-if="complianceReport.pointsInsidePolygons" class="mb-1 warning--text">{{ $t('eudrCompliancePointsInside', { count: complianceReport.pointsInsidePolygons }) }}</p>
            <p v-if="complianceReport.pointsOutsidePolygons" class="mb-1 warning--text">{{ $t('eudrCompliancePointsOutside', { count: complianceReport.pointsOutsidePolygons }) }}</p>
            <p v-if="complianceReport.nonNumericAreas" class="mb-1">{{ $t('eudrComplianceNonNumericAreas', { count: complianceReport.nonNumericAreas }) }}</p>
            <p v-if="complianceReport.invalidCountries" class="mb-1">{{ $t('eudrComplianceInvalidCountries', { count: complianceReport.invalidCountries }) }}</p>
            <p v-if="complianceReport.selfIntersecting.length" class="mb-1 error--text">{{ $t('eudrComplianceSelfIntersecting', { names: complianceReport.selfIntersecting.join(', ') }) }}</p>
            <p v-if="complianceReport.invalidFeatures.length" class="mb-1 error--text">{{ $t('eudrComplianceInvalid', { names: complianceReport.invalidFeatures.join(', ') }) }}</p>
            <p v-if="!complianceReport.sizeOk" class="mb-1 error--text">{{ $t('eudrComplianceTooLarge') }}</p>
          </div>
          <p class="caption grey--text text--darken-1 mb-0 mt-2">{{ $t('eudrFinalizeConfirm') }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="cancelFinalize">{{ $t('cancel') }}</v-btn>
          <v-btn
            color="success"
            :loading="finalizing"
            :disabled="complianceReport.blocking || finalizing"
            @click="confirmFinalize"
          >{{ $t('eudrFinalizeTitle') }}</v-btn>
        </v-card-actions>
      </v-card>
    </Dialog>

    <Dialog :stateId="resetDialogId" @close="cancelReset" @click:outside="cancelReset" @dialog-closing="cancelReset">
      <v-card v-if="promptingReset">
        <v-card-title class="primary white--text">{{ $t('eudrResetValidation') }}</v-card-title>
        <v-card-text class="pt-4">{{ $t('eudrResetValidationConfirm') }}</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="cancelReset">{{ $t('cancel') }}</v-btn>
          <v-btn color="error" :loading="resetting" @click="confirmReset">{{ $t('eudrResetValidation') }}</v-btn>
        </v-card-actions>
      </v-card>
    </Dialog>

    <Dialog :stateId="shapeEditorDialogId" :confirmClose="editingShapes" @close="onShapeEditorClosed" @dialog-closing="onShapeEditorClosed">
      <EudrShapeEditor
        v-if="editingShapes"
        :featureCollection="workingCollection"
        :initialFeatureId="editingFeatureId"
        @apply="onShapesApplied"
        @close="closeShapeEditor"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { EudrBatchStatus } from '@/utils/Enumerations.js'
import { eudrClient } from '@/utils/EudrClient.js'
import { analyzeFeatureCollection, BLOCKING_WARNINGS, POINT_COVERED_WARNING, createPointReconciler } from '@/utils/eudr/EudrGeometry.js'
import {
  lookupParcelAtPoint, applyParcelToPointFeature, applyBufferToPointFeature, canRevertToPoint, revertFeatureToPoint, movePointFeature
} from '@/utils/eudr/EudrPointResolve.js'
import { findOverlaps, OVERLAP_WARNING } from '@/utils/eudr/EudrOverlap.js'
import { sanitizeForSubmission } from '@/utils/eudr/EudrExport.js'
import { featureDisplayName, isSyntheticParcelId, tractTypeLabel } from '@/utils/eudr/EudrFeatureName.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

// Sections bigger than this start collapsed; mounting a thousand rows freezes the first paint.
const LARGE_SECTION_ROWS = 200

export default {
  name: 'EudrValidationDialog',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    EudrMap: () => import('@/components/eudr/EudrMap.vue'),
    EudrShapeEditor: () => import('@/components/eudr/EudrShapeEditor.vue')
  },

  props: {
    // The composite batch row from the manager grid.
    report: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      loading: true,
      loadError: false,
      saving: false,
      resetting: false,
      finalizing: false,
      // Batch status as of the latest in-dialog save; null until one happens (see reportStatus).
      statusOverride: null,
      promptingReset: false,
      promptingFinalize: false,
      editingShapes: false,
      editingFeatureId: null,
      // The feature the list and the remounted map return to after a geometry change.
      focusedFeatureId: null,
      geometryEdited: false,
      fetchingPointId: null,
      // Keys pre-declared so Vue tracks the toggles.
      collapsedSections: { contested: false, other: false, covered: false, unmatched: false },
      // Meters: unmatched points closer than this to a declared shape are treated as covered by it.
      nearPointThreshold: 100,
      mapKey: 0,
      workingCollection: null,
      rows: [],
      excludedIds: [],
      initialExcludedIds: [],
      geometryWarnings: {},
      complianceReport: null,
      sanitizedSerialized: '',
      resetDialogId: uniqueDialogId('eudr-validation-reset-dialog'),
      finalizeDialogId: uniqueDialogId('eudr-validation-finalize-dialog'),
      shapeEditorDialogId: uniqueDialogId('eudr-validation-shape-editor-dialog')
    }
  },

  computed: {
    title () {
      return `${this.report.label || this.$t('untitled')} — ${this.$t('eudrReviewTitle')}`
    },

    // The prop is a stale pre-open snapshot; statusOverride tracks saves made while the dialog is open.
    reportStatus () {
      return this.statusOverride ?? this.report.status
    },

    isFinalized () {
      return this.reportStatus === EudrBatchStatus.Finalized.value
    },

    canReset () {
      return this.reportStatus === EudrBatchStatus.Validated.value || this.isFinalized
    },

    hasGeometry () {
      return (this.workingCollection?.features?.length ?? 0) > 0
    },

    excludedSet () {
      return new Set(this.excludedIds)
    },

    includedCount () {
      return this.rows.length - this.excludedIds.length
    },

    // One group per parcel, area shapes only; contested parcels surface in their own section at the top.
    parcelGroups () {
      const byParcel = new Map()
      for (const row of this.rows) {
        if (row.isPoint) continue
        const key = row.parcelId ?? ''
        if (!byParcel.has(key)) byParcel.set(key, { parcelId: key, conflict: false, features: [] })
        byParcel.get(key).features.push(row)
      }
      // Contested only while 2+ conflicted shapes remain INCLUDED, so excluding down to one resolves it.
      for (const group of byParcel.values()) {
        group.conflict = group.features.filter(f => f.conflict === true && !this.excludedSet.has(f.featureId)).length >= 2
      }
      // Sort inside a group too: parcelId-less features share one anonymous group that would bury them.
      for (const group of byParcel.values()) {
        group.features.sort((a, b) => {
          const severity = this.featureSeverity(a.featureId) - this.featureSeverity(b.featureId)
          if (severity !== 0) return severity
          if (a.origin !== b.origin) return a.origin === 'internal' ? -1 : 1
          return String(a.displayName).localeCompare(String(b.displayName))
        })
      }
      // groupSeverity reads geometryWarnings, so this re-sorts after a shape edit.
      return [...byParcel.values()]
        .map(group => ({ ...group, label: this.parcelGroupLabel(group) }))
        .sort((a, b) => {
          const severity = this.groupSeverity(a) - this.groupSeverity(b)
          if (severity !== 0) return severity
          return String(a.parcelId).localeCompare(String(b.parcelId))
        })
    },

    // Covered points sit inside a declared shape, so their ground is already in the report.
    coveredPointRows () {
      return this.rows
        .filter(r => r.isPoint && r.covered)
        .slice()
        .sort((a, b) => this.pointRowSeverity(a) - this.pointRowSeverity(b))
    },

    unmatchedPointRows () {
      return this.rows
        .filter(r => r.isPoint && !r.covered)
        .slice()
        .sort((a, b) =>
          (this.pointRowSeverity(a) - this.pointRowSeverity(b)) ||
          ((a.nearestDistanceMeters ?? Infinity) - (b.nearestDistanceMeters ?? Infinity)))
    },

    coveredIncludedCount () {
      return this.coveredPointRows.filter(r => !this.excludedSet.has(r.featureId)).length
    },

    sections () {
      const pointGroup = (key, features) => features.length
        ? [{ parcelId: key, label: null, conflict: false, features }]
        : []
      const withCount = (section) => ({
        ...section,
        count: section.groups.reduce((sum, group) => sum + group.features.length, 0)
      })
      // Same-coordinate included points beyond the first: the count the exclude-duplicates button shows.
      const pointDupes = (rows) => {
        const seen = new Map()
        for (const row of rows) {
          if (!row.pointKey || this.excludedSet.has(row.featureId)) continue
          seen.set(row.pointKey, (seen.get(row.pointKey) ?? 0) + 1)
        }
        return [...seen.values()].reduce((sum, n) => sum + Math.max(n - 1, 0), 0)
      }
      return [
        { key: 'contested', title: this.$t('eudrContestedParcels'), groups: this.parcelGroups.filter(g => g.conflict) },
        { key: 'other', title: this.$t('eudrOtherParcels'), groups: this.parcelGroups.filter(g => !g.conflict) },
        { key: 'covered', title: this.$t('eudrCoveredPoints'), groups: pointGroup('covered-points', this.coveredPointRows), dupeCount: pointDupes(this.coveredPointRows) },
        { key: 'unmatched', title: this.$t('eudrUnmatchedPoints'), groups: pointGroup('unmatched-points', this.unmatchedPointRows), dupeCount: pointDupes(this.unmatchedPointRows) }
      ].map(withCount)
    },

    nearPointExcludeEnabled () {
      return Number.isFinite(this.nearPointThreshold) && this.nearPointThreshold > 0
    },

    remainingUnmatchedCount () {
      return this.unmatchedPointRows.filter(r => !this.excludedSet.has(r.featureId)).length
    },

    contestedParcelCount () {
      return this.parcelGroups.filter(g => g.conflict).length
    },

    isDirty () {
      if (this.geometryEdited) return true
      if (this.excludedIds.length !== this.initialExcludedIds.length) return true
      const initial = new Set(this.initialExcludedIds)
      return this.excludedIds.some(id => !initial.has(id))
    },

    complianceSizeLabel () {
      if (!this.complianceReport) return ''
      return `${(this.complianceReport.sizeBytes / (1024 * 1024)).toFixed(1)} MB`
    }
  },

  created () {
    // Non-reactive on purpose (a timer handle re-rendering nothing); declared here so it's discoverable.
    this.thresholdDebounce = null
    this.load()
  },

  mounted () {
    document.addEventListener('keydown', this.onKeyDown)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.onKeyDown)
    clearTimeout(this.thresholdDebounce)
  },

  methods: {
    ...mapActions('eudr', ['fetchWriteSas', 'saveValidation', 'resetValidation', 'finalizeBatch']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    // Load the validated copy when one exists (so prior decisions show), else the daemon's original.
    async load () {
      this.loading = true
      this.loadError = false
      try {
        const path = this.report.validatedBlobPath || this.report.resultBlobPath
        const blob = await eudrClient.fetchReportBlob(path)
        const parsed = JSON.parse(await blob.text())
        // Let the loading spinner paint before the synchronous analysis pass.
        await new Promise(resolve => setTimeout(resolve))
        const features = parsed?.features ?? []
        this.workingCollection = { type: 'FeatureCollection', features }
        this.rebuildRowsAndWarnings({ trustStamps: true })
        // Without the null filter, undefined in the set makes persistValidation exclude every id-less feature.
        this.excludedIds = features
          .filter(f => f.properties?.excluded === true)
          .map(f => f.properties.featureId)
          .filter(id => id != null)
        this.initialExcludedIds = [...this.excludedIds]
        // Oversized sections start collapsed: mounting a thousand rows at once freezes the dialog open.
        for (const section of this.sections) {
          this.collapsedSections[section.key] = section.count > LARGE_SECTION_ROWS
        }
      } catch (e) {
        this.loadError = true
      } finally {
        this.loading = false
      }
    },

    // trustStamps: persisted verdicts only go stale on in-session geometry edits, so loads skip the recompute.
    rebuildRowsAndWarnings ({ trustStamps = false } = {}) {
      const features = this.workingCollection?.features ?? []
      // EU-rule pre-check so problem shapes are visible before the user ever reaches Finalize.
      const analysis = analyzeFeatureCollection(this.workingCollection)
      const warnings = {}
      for (const [featureId, codes] of analysis.byFeature) warnings[featureId] = codes
      // Overlaps are a collection-level check analyzeFeature can't see; exclusion doesn't move the ground.
      const { overlappingIndices } = findOverlaps(this.workingCollection)
      for (const index of overlappingIndices) {
        const featureId = features[index]?.properties?.featureId
        if (featureId == null) continue
        warnings[featureId] = [...(warnings[featureId] ?? []), OVERLAP_WARNING]
      }
      // Reconcile live: any geometry edit here can change a point's verdict.
      // Bbox-prefiltered and built lazily; naive per-point scans blocked the UI for ~a second at composite scale.
      const polygonal = features.filter(f => ['Polygon', 'MultiPolygon'].includes(f.geometry?.type))
      let reconciler = null
      const getReconciler = () => (reconciler = reconciler ?? createPointReconciler(polygonal))
      const pointInfo = {}
      for (const feature of features) {
        if (feature.geometry?.type !== 'Point') continue
        const props = feature.properties ?? {}
        const featureId = props.featureId
        if (featureId == null) continue
        // Unstamped points (pre-stamping blobs, or no polygons to measure against) fall through to recompute.
        if (trustStamps && (props.capturedByFeatureId != null || props.nearestDistanceMeters != null)) {
          if (props.capturedByFeatureId != null) {
            pointInfo[featureId] = { covered: true, coveredByParcelId: props.capturedByParcelId ?? null, coveredByFeatureId: props.capturedByFeatureId }
            warnings[featureId] = [...(warnings[featureId] ?? []), POINT_COVERED_WARNING]
          } else {
            pointInfo[featureId] = {
              covered: false,
              nearestParcelId: props.nearestParcelId ?? null,
              nearestFeatureId: props.nearestFeatureId ?? null,
              nearestDistanceMeters: props.nearestDistanceMeters
            }
          }
          continue
        }
        const [lon, lat] = feature.geometry.coordinates ?? []
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue
        const captor = getReconciler().captorFor(lon, lat)
        if (captor) {
          pointInfo[featureId] = {
            covered: true,
            coveredByParcelId: captor.properties?.parcelId ?? null,
            coveredByFeatureId: captor.properties?.featureId ?? null
          }
          warnings[featureId] = [...(warnings[featureId] ?? []), POINT_COVERED_WARNING]
          continue
        }
        const nearest = getReconciler().nearestTo(lon, lat)
        pointInfo[featureId] = {
          covered: false,
          nearestParcelId: nearest?.feature?.properties?.parcelId ?? null,
          nearestFeatureId: nearest?.feature?.properties?.featureId ?? null,
          nearestDistanceMeters: nearest?.distanceMeters ?? null
        }
      }
      this.rows = features.map(f => {
        const p = f.properties ?? {}
        return {
          featureId: p.featureId,
          parcelId: p.parcelId,
          productionPlace: p.productionPlace,
          displayName: featureDisplayName(f),
          accountName: p.accountName,
          origin: p.origin,
          uploadKind: p.uploadKind,
          tractType: p.tractType,
          conflict: p.conflict === true,
          isPoint: f.geometry?.type === 'Point',
          canRevert: canRevertToPoint(f),
          // Duplicate detection key: two points at the same (already 6dp-rounded) coordinate are dupes.
          pointKey: f.geometry?.type === 'Point' ? (f.geometry.coordinates ?? []).join(',') : null,
          // Every account behind a daemon-merged duplicate point; null for ordinary features.
          accountNames: Array.isArray(p.accountNames) ? p.accountNames : null,
          covered: false,
          coveredByParcelId: null,
          coveredByFeatureId: null,
          nearestParcelId: null,
          nearestFeatureId: null,
          nearestDistanceMeters: null,
          ...(pointInfo[p.featureId] ?? {})
        }
      })
      this.geometryWarnings = warnings
    },

    warningsFor (featureId) {
      return this.geometryWarnings[featureId] ?? []
    },

    hasBlockingWarning (featureId) {
      return this.warningsFor(featureId).some(code => BLOCKING_WARNINGS.includes(code))
    },

    // 0 = blocking, 1 = warned, 2 = clean. POINT_COVERED_WARNING is skipped: every covered point carries it.
    pointRowSeverity (row) {
      if (this.hasBlockingWarning(row.featureId)) return 0
      return this.warningsFor(row.featureId).some(code => code !== POINT_COVERED_WARNING) ? 1 : 2
    },

    // 0 = has a blocking warning, 1 = has a non-blocking warning, 2 = clean. Lower sorts first.
    featureSeverity (featureId) {
      if (this.hasBlockingWarning(featureId)) return 0
      return this.warningsFor(featureId).length ? 1 : 2
    },

    // A group is as severe as its worst feature.
    groupSeverity (group) {
      return group.features.reduce((worst, f) => Math.min(worst, this.featureSeverity(f.featureId)), 2)
    },

    warningTooltip (featureId) {
      const labels = this.warningsFor(featureId)
        .map(code => this.$t(`eudrGeometry${code.charAt(0).toUpperCase()}${code.slice(1)}`))
      return `${this.$t('eudrGeometryWarningBadge')}: ${labels.join(', ')}`
    },

    isExcluded (featureId) {
      return this.excludedSet.has(featureId)
    },

    toggle (featureId) {
      const nowExcluded = !this.isExcluded(featureId)
      this.excludedIds = nowExcluded
        ? [...this.excludedIds, featureId]
        : this.excludedIds.filter(id => id !== featureId)
      if (this.$refs.map) this.$refs.map.setFeatureExcluded(featureId, nowExcluded)
    },

    zoomTo (feature) {
      this.focusedFeatureId = feature.featureId
      if (this.$refs.map) this.$refs.map.zoomToFeatureId(feature.featureId)
    },

    // Keyboard review: ↑/↓ rows, Space toggle, Enter re-zoom, E edit, F fetch parcel, D draw area, R revert.
    onKeyDown (e) {
      if (this.loading || this.loadError) return
      if (this.editingShapes || this.promptingFinalize || this.promptingReset) return
      const tag = e.target?.tagName
      if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(tag) || e.target?.isContentEditable) return
      if (e.ctrlKey || e.metaKey || e.altKey) return

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault()
        this.moveFocus(e.key === 'ArrowDown' ? 1 : -1)
        return
      }
      if (this.focusedFeatureId == null) return
      const row = this.rows.find(r => r.featureId === this.focusedFeatureId)
      switch (e.key) {
        case ' ':
          e.preventDefault()
          this.toggle(this.focusedFeatureId)
          break
        case 'Enter':
          e.preventDefault()
          if (this.$refs.map) this.$refs.map.zoomToFeatureId(this.focusedFeatureId)
          break
        case 'e':
        case 'E':
          this.openShapeEditorFor(this.focusedFeatureId)
          break
        case 'f':
        case 'F':
          if (row?.isPoint && !row.covered) this.fetchParcelForPoint(this.focusedFeatureId)
          break
        case 'd':
        case 'D':
          if (row?.isPoint && !row.covered) this.drawAreaForPoint(this.focusedFeatureId)
          break
        case 'r':
        case 'R':
          if (row?.canRevert) this.revertToPoint(this.focusedFeatureId)
          break
        default: break
      }
    },

    // Visible rows in render order; collapsed sections are skipped, so they leave the keyboard path.
    navigableRows () {
      const rows = []
      for (const section of this.sections) {
        if (this.collapsedSections[section.key]) continue
        for (const group of section.groups) rows.push(...group.features)
      }
      return rows
    },

    moveFocus (delta) {
      const rows = this.navigableRows()
      if (!rows.length) return
      const index = rows.findIndex(r => r.featureId === this.focusedFeatureId)
      const next = index === -1
        ? (delta > 0 ? 0 : rows.length - 1)
        : Math.min(Math.max(index + delta, 0), rows.length - 1)
      const row = rows[next]
      if (row.featureId === this.focusedFeatureId) return
      this.focusedFeatureId = row.featureId
      this.scrollFocusedIntoView()
      if (this.$refs.map) this.$refs.map.zoomToFeatureId(row.featureId)
    },

    // The section a feature's row currently renders in (rows move as points resolve and conflicts clear).
    sectionKeyFor (featureId) {
      const row = this.rows.find(r => r.featureId === featureId)
      if (!row) return null
      if (row.isPoint) return row.covered ? 'covered' : 'unmatched'
      const group = this.parcelGroups.find(g => g.features.some(f => f.featureId === featureId))
      return group?.conflict ? 'contested' : 'other'
    },

    // Restore the reviewer's place after a geometry change; a deleted feature drops the focus instead.
    restoreFocus () {
      const featureId = this.focusedFeatureId
      if (featureId == null) return
      if (!this.rows.some(r => r.featureId === featureId)) {
        this.focusedFeatureId = null
        return
      }
      const sectionKey = this.sectionKeyFor(featureId)
      if (sectionKey && this.collapsedSections[sectionKey]) this.collapsedSections[sectionKey] = false
      this.scrollFocusedIntoView()
    },

    scrollFocusedIntoView () {
      const featureId = this.focusedFeatureId
      this.$nextTick(() => {
        const refs = this.$refs['row-' + featureId]
        const el = Array.isArray(refs) ? refs[0] : refs
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    },

    // A synthetic `point:<lon>,<lat>` parcelId is an upload requirement, not a real parcel number.
    parcelGroupLabel (group) {
      return isSyntheticParcelId(group.parcelId)
        ? `${this.$t('eudrPointLocation')}: ${String(group.parcelId).slice('point:'.length)}`
        : `${this.$t('parcelNumber')}: ${group.parcelId}`
    },

    featureCaption (feature) {
      const parts = []
      // A daemon-merged duplicate point carries every contributing account.
      const accountLabel = feature.accountNames?.length > 1 ? feature.accountNames.join(', ') : feature.accountName
      if (accountLabel) parts.push(accountLabel)
      parts.push(feature.origin === 'internal' ? this.$t('eudrOriginInternal') : this.$t('eudrOriginExternal'))
      const tractType = tractTypeLabel(feature.tractType)
      if (tractType) parts.push(tractType)
      if (feature.uploadKind === 'csvParcel') parts.push(this.$t('eudrUploadKindCsvParcel'))
      if (feature.uploadKind === 'csvPoint') parts.push(this.$t('eudrUploadKindCsvPoint'))
      if (feature.uploadKind === 'csvPointBuffer') parts.push(this.$t('eudrUploadKindCsvPointBuffer'))
      if (feature.uploadKind === 'csvPointDrawn') parts.push(this.$t('eudrUploadKindCsvPointDrawn'))
      // Synthetic point:<lon>,<lat> ids must never read as parcel numbers.
      if (feature.isPoint && feature.covered) {
        parts.push(feature.coveredByParcelId && !isSyntheticParcelId(feature.coveredByParcelId)
          ? this.$t('eudrPointInsideParcel', { parcelId: feature.coveredByParcelId })
          : this.$t('eudrPointInsideShape'))
      } else if (feature.isPoint && feature.nearestDistanceMeters != null) {
        const distance = this.formatDistance(feature.nearestDistanceMeters)
        parts.push(feature.nearestParcelId && !isSyntheticParcelId(feature.nearestParcelId)
          ? this.$t('eudrPointDistanceFromParcel', { distance, parcelId: feature.nearestParcelId })
          : this.$t('eudrPointDistanceFromShape', { distance }))
      }
      return parts.join(' · ')
    },

    formatDistance (meters) {
      if (meters >= 1000) return `${(meters / 1000).toFixed(1)} km`
      // Sub-meter distances keep a decimal; stamps carry 0.1 m precision, so "0 m" would be a lie.
      if (meters < 1) return `${meters.toFixed(1)} m`
      return `${Math.round(meters)} m`
    },

    toggleSection (key) {
      this.collapsedSections[key] = !this.collapsedSections[key]
    },

    // Immediate on blur/Enter so the value lands before a click that follows the blur; 300ms otherwise.
    onThresholdInput (value, { immediate = false } = {}) {
      clearTimeout(this.thresholdDebounce)
      const commit = () => { this.nearPointThreshold = Number(value) }
      if (immediate) commit()
      else this.thresholdDebounce = setTimeout(commit, 300)
    },

    // Reversible; points with no shape to measure against are left alone.
    excludeNearPoints () {
      if (!this.nearPointExcludeEnabled) return
      const threshold = this.nearPointThreshold
      const excluded = new Set(this.excludedIds)
      let count = 0
      for (const row of this.unmatchedPointRows) {
        if (excluded.has(row.featureId)) continue
        if (row.nearestDistanceMeters == null || row.nearestDistanceMeters >= threshold) continue
        excluded.add(row.featureId)
        if (this.$refs.map) this.$refs.map.setFeatureExcluded(row.featureId, true)
        count++
      }
      this.excludedIds = [...excluded]
      this.setSnack(count > 0
        ? this.$t('eudrNearPointsExcluded', { count, threshold })
        : this.$t('eudrNoNearPoints', { threshold }))
    },

    // Keeps one point per coordinate, a named tract beating an unnamed row to match the CSV merge rule.
    excludeDuplicatePoints (sectionKey) {
      const rows = (sectionKey === 'covered' ? this.coveredPointRows : this.unmatchedPointRows)
        .filter(r => r.pointKey && !this.excludedSet.has(r.featureId))
      const byCoordinate = new Map()
      for (const row of rows) {
        if (!byCoordinate.has(row.pointKey)) byCoordinate.set(row.pointKey, [])
        byCoordinate.get(row.pointKey).push(row)
      }
      const excluded = new Set(this.excludedIds)
      let count = 0
      for (const group of byCoordinate.values()) {
        if (group.length < 2) continue
        const keeper = group.find(r => r.productionPlace) ?? group[0]
        for (const row of group) {
          if (row === keeper) continue
          excluded.add(row.featureId)
          if (this.$refs.map) this.$refs.map.setFeatureExcluded(row.featureId, true)
          count++
        }
      }
      this.excludedIds = [...excluded]
      if (count > 0) this.setSnack(this.$t('eudrDupePointsExcluded', { count }))
    },

    // Same ~4 ha artificial polygon the per-point Draw area action creates.
    drawAreasForRemaining () {
      const remaining = this.unmatchedPointRows.filter(r => !this.excludedSet.has(r.featureId))
      let count = 0
      for (const row of remaining) {
        const feature = this.workingCollection?.features?.find(f => f.properties?.featureId === row.featureId)
        if (!feature || feature.geometry?.type !== 'Point') continue
        if (applyBufferToPointFeature(feature)) count++
      }
      if (count === 0) return
      this.onPointResolved()
      this.setSnack(this.$t('eudrAreasDrawnBulk', { count }))
    },

    // Covered ground is already declared by the containing shape, so excluding is the normal resolution.
    excludeAllCovered () {
      const excluded = new Set(this.excludedIds)
      for (const row of this.coveredPointRows) {
        if (excluded.has(row.featureId)) continue
        excluded.add(row.featureId)
        if (this.$refs.map) this.$refs.map.setFeatureExcluded(row.featureId, true)
      }
      this.excludedIds = [...excluded]
    },

    // Resolve an unmatched point into the real parcel boundary at its coordinate (ReportAll lookup).
    async fetchParcelForPoint (featureId) {
      if (this.fetchingPointId) return
      const feature = this.workingCollection?.features?.find(f => f.properties?.featureId === featureId)
      if (!feature || feature.geometry?.type !== 'Point') return
      const [longitude, latitude] = feature.geometry.coordinates ?? []
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return

      this.fetchingPointId = featureId
      try {
        const parcel = await lookupParcelAtPoint(longitude, latitude)
        if (!parcel) {
          this.setSnackError(this.$t('eudrNoParcelAtPoint'))
          return
        }
        applyParcelToPointFeature(feature, parcel)
        this.focusedFeatureId = featureId
        this.onPointResolved()
        this.setSnack(this.$t('eudrParcelFetched', { parcelId: parcel.parcelId }))
      } catch (e) {
        this.setSnackError(this.$t('eudrParcelLookupFailed'))
      } finally {
        this.fetchingPointId = null
      }
    },

    // Resolve an unmatched point into a small artificial polygon around it (no parcel lookup involved).
    drawAreaForPoint (featureId) {
      const feature = this.workingCollection?.features?.find(f => f.properties?.featureId === featureId)
      if (!feature || feature.geometry?.type !== 'Point') return
      if (!applyBufferToPointFeature(feature)) return
      this.focusedFeatureId = featureId
      this.onPointResolved()
      this.setSnack(this.$t('eudrAreaDrawn'))
    },

    // Undo a fetch or drawn area: the shape becomes the original point again and rejoins the point sections.
    revertToPoint (featureId) {
      const feature = this.workingCollection?.features?.find(f => f.properties?.featureId === featureId)
      if (!feature || !revertFeatureToPoint(feature)) return
      this.focusedFeatureId = featureId
      this.onPointResolved()
      this.setSnack(this.$t('eudrRevertedToPoint'))
    },

    // Dragged on the review map. The flag already sits at the new spot, so the map is synced, not remounted.
    onPointMoved ({ featureId, coordinates }) {
      const feature = this.workingCollection?.features?.find(f => f.properties?.featureId === featureId)
      if (!feature || !movePointFeature(feature, coordinates[0], coordinates[1])) {
        if (feature && this.$refs.map) this.$refs.map.syncFeature(featureId, feature)
        return
      }
      this.rebuildRowsAndWarnings()
      for (const f of this.workingCollection.features) {
        if (f.properties) f.properties.excluded = this.excludedSet.has(f.properties.featureId)
      }
      this.geometryEdited = true
      this.focusedFeatureId = featureId
      if (this.$refs.map) this.$refs.map.syncFeature(featureId, feature)
      this.restoreFocus()
      this.setSnack(this.$t('eudrPointMoved'))
    },

    // A point became an area shape (or the reverse), so it changes sections.
    onPointResolved () {
      this.rebuildRowsAndWarnings()
      for (const feature of this.workingCollection.features) {
        if (feature.properties) feature.properties.excluded = this.excludedSet.has(feature.properties.featureId)
      }
      this.geometryEdited = true
      this.mapKey++
      this.restoreFocus()
    },

    // Saving flips the batch to Validated and invalidates any previously finalized EU file.
    // Verdicts are re-stamped so the next open can trust them instead of recomputing.
    async persistValidation () {
      const pointRows = new Map(this.rows.filter(r => r.isPoint).map(r => [r.featureId, r]))
      for (const feature of this.workingCollection.features) {
        const props = feature.properties
        // A featureId-less feature has no row to toggle; resolving it against the set would false-positive.
        if (!props || props.featureId == null) continue
        props.excluded = this.excludedSet.has(props.featureId)
        const row = pointRows.get(props.featureId)
        if (!row) continue
        if (row.covered) {
          props.capturedByParcelId = row.coveredByParcelId ?? null
          props.capturedByFeatureId = row.coveredByFeatureId ?? null
          delete props.nearestParcelId
          delete props.nearestFeatureId
          delete props.nearestDistanceMeters
        } else {
          props.nearestParcelId = row.nearestParcelId ?? null
          props.nearestFeatureId = row.nearestFeatureId ?? null
          props.nearestDistanceMeters = row.nearestDistanceMeters ?? null
          delete props.capturedByParcelId
          delete props.capturedByFeatureId
        }
      }
      const sas = await this.fetchWriteSas({ id: this.report.eudrBatchId, artifact: 'validated' })
      await eudrClient.uploadBlobWithSas(sas.sasUri, JSON.stringify(this.workingCollection))
      const updated = await this.saveValidation({ id: this.report.eudrBatchId, excludedFeatureCount: this.excludedIds.length })
      this.statusOverride = updated?.status ?? EudrBatchStatus.Validated.value
      this.initialExcludedIds = [...this.excludedIds]
      this.geometryEdited = false
      this.setSnack(this.$t('eudrValidationSaved'))
    },

    async save () {
      this.saving = true
      try {
        await this.persistValidation()
        this.$emit('saved')
      } catch (e) {
        this.setSnackError(this.$t('eudrValidationSaveFailed'))
      } finally {
        this.saving = false
      }
    },

    // Persist first so the finalized artifact always corresponds to a saved validation state.
    async openFinalize () {
      this.finalizing = true
      try {
        if (this.isDirty) {
          await this.persistValidation()
          this.$emit('refresh')
        }
        const { serialized, report } = sanitizeForSubmission(this.workingCollection, this.excludedSet)
        this.complianceReport = report
        this.sanitizedSerialized = serialized
        this.promptingFinalize = true
        this.openOrUpdateDialog({ id: this.finalizeDialogId, width: '560px' })
      } catch (e) {
        this.setSnackError(this.$t('eudrFinalizeFailed'))
      } finally {
        this.finalizing = false
      }
    },

    cancelFinalize () {
      this.promptingFinalize = false
      this.complianceReport = null
      this.sanitizedSerialized = ''
      this.closeDialogsAtOrAbove(this.finalizeDialogId)
    },

    async confirmFinalize () {
      this.finalizing = true
      try {
        const sas = await this.fetchWriteSas({ id: this.report.eudrBatchId, artifact: 'sanitized' })
        await eudrClient.uploadBlobWithSas(sas.sasUri, this.sanitizedSerialized)
        await this.finalizeBatch(this.report.eudrBatchId)
        this.setSnack(this.$t('eudrFinalized'))
        this.cancelFinalize()
        this.$emit('saved')
      } catch (e) {
        this.setSnackError(this.$t('eudrFinalizeFailed'))
      } finally {
        this.finalizing = false
      }
    },

    promptReset () {
      this.promptingReset = true
      this.openOrUpdateDialog({ id: this.resetDialogId, width: '420px' })
    },

    cancelReset () {
      this.promptingReset = false
      this.closeDialogsAtOrAbove(this.resetDialogId)
    },

    async confirmReset () {
      this.resetting = true
      try {
        await this.resetValidation(this.report.eudrBatchId)
        // Success snackbar fired by the response interceptor (customResponses.validation.delete).
        this.cancelReset()
        this.$emit('saved')
      } catch (e) {
        // Keep the dialog open.
      } finally {
        this.resetting = false
      }
    },

    openShapeEditor () {
      this.openShapeEditorFor(null)
    },

    // Deep-link entry: a row's edit icon or a double-clicked polygon opens the editor on that shape.
    openShapeEditorFor (featureId) {
      this.editingFeatureId = featureId ?? null
      if (featureId != null) this.focusedFeatureId = featureId
      this.editingShapes = true
      this.openOrUpdateDialog({ id: this.shapeEditorDialogId, width: '90vw' })
    },

    // Requests the close; the Dialog runs the confirm-close flow and emits close if accepted.
    closeShapeEditor () {
      this.closeDialogsAtOrAbove(this.shapeEditorDialogId)
    },

    onShapeEditorClosed () {
      this.editingShapes = false
      this.editingFeatureId = null
    },

    // The editor only changes geometry or removes whole features.
    onShapesApplied (updated) {
      this.workingCollection = updated
      this.rebuildRowsAndWarnings()
      const validIds = new Set(this.rows.map(r => r.featureId))
      this.excludedIds = this.excludedIds.filter(id => validIds.has(id))
      // Map styling reads feature properties, which only carry the last PERSISTED exclusion state.
      for (const feature of updated.features) {
        if (feature.properties) feature.properties.excluded = this.excludedSet.has(feature.properties.featureId)
      }
      this.geometryEdited = true
      this.mapKey++
      this.restoreFocus()
      // Tearing down first drops confirmClose, so the applied path closes without prompting.
      this.onShapeEditorClosed()
      this.closeShapeEditor()
    }
  }
}
</script>

<style>
/* Unscopable (v-tooltip detaches its content); idempotently duplicated from EudrShapeEditor. */
.eudr-reduce-info-tooltip {
  display: inline-block;
  max-width: 340px;
}
</style>
<style scoped>
.eudr-validation-panel {
  height: calc(70vh - 45px);
  overflow-y: auto;
}
.eudr-section-header {
  cursor: pointer;
  user-select: none;
}
.eudr-section-header:hover {
  background: rgba(0, 0, 0, 0.04);
}
.eudr-distance-threshold {
  max-width: 110px;
}
.eudr-feature-row {
  cursor: pointer;
}
.eudr-feature-row:hover {
  background: rgba(0, 0, 0, 0.04);
}
.eudr-feature-focused {
  background: rgba(209, 95, 39, 0.12);
}
.eudr-feature-excluded .body-2,
.eudr-feature-excluded .caption {
  text-decoration: line-through;
  opacity: 0.6;
}
.eudr-parcel-group + .eudr-parcel-group {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
